import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { REVIEWS } from '../constants/reviews';
import { Reveal, Overline, EASE } from './motion';

export const GoogleLogo = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82Z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24Z" />
    <path fill="#FBBC05" d="M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09Z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42A11.98 11.98 0 0 0 12 0 11.99 11.99 0 0 0 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75Z" />
  </svg>
);

export const Stars = ({ size = 14 }) => (
  <span className="flex gap-0.5 text-[#FBBC05]">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={size} fill="currentColor" strokeWidth={0} />
    ))}
  </span>
);

export const TestimonialStrip = () => {
  const { t } = useLanguage();
  return (
    <section data-testid="testimonial-strip" className="border-b border-line/10 bg-surface">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
        <span className="flex items-center gap-3">
          <GoogleLogo size={22} />
          <span className="font-display text-2xl font-semibold">4.9</span>
          <Stars size={16} />
        </span>
        <span className="text-smoke text-sm">{t('testimonials.basedOn')}</span>
      </div>
    </section>
  );
};

export const Testimonials = () => {
  const { t } = useLanguage();
  const ts = t('testimonials');
  const [start, setStart] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setStart((s) => (s + 1) % REVIEWS.length), 4500);
    return () => clearInterval(id);
  }, [paused]);

  const visible = [...Array(4)].map((_, i) => REVIEWS[(start + i) % REVIEWS.length]);

  return (
    <section data-testid="testimonials-section" className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
      <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <Overline className="mb-4">{ts.overline}</Overline>
          <h2 className="font-display text-3xl md:text-5xl font-medium tracking-tight">{ts.title}</h2>
        </div>
        <div className="flex items-center gap-4 border border-line/10 bg-surface px-6 py-4">
          <GoogleLogo size={28} />
          <span>
            <span className="flex items-center gap-2">
              <span className="font-display text-2xl font-semibold">4.9</span>
              <Stars size={15} />
            </span>
            <span className="text-smoke text-xs block mt-1">{ts.basedOn}</span>
          </span>
        </div>
      </Reveal>
      <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        <motion.div
          key={start}
          initial={{ opacity: 0, x: 48 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-line/10"
        >
          {visible.map((r, i) => (
            <figure key={`${r.name}-${i}`} data-testid={`testimonial-${i}`} className="bg-paper p-6 md:p-8 h-full flex flex-col hover:bg-surface transition-colors duration-500">
              <div className="flex items-center justify-between mb-5">
                <Stars />
                <GoogleLogo size={16} />
              </div>
              <blockquote className="text-sm leading-relaxed flex-1">„{r.t}"</blockquote>
              <figcaption className="mt-6 pt-5 border-t border-line/10">
                <span className="font-display font-medium block">{r.name}</span>
                <span className="text-smoke text-xs uppercase tracking-[0.12em] mt-1 block">{r.meta}</span>
              </figcaption>
            </figure>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

